import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Plus, Minus, ShoppingBag, Send, Popcorn } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeatOrder() {
  const { showtimeId, seatId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [cart, setCart] = useState({}); // { productId: quantity }
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${API}/seat-order/${showtimeId}/${seatId}`)
      .then((r) => setCtx(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Vorstellung nicht gefunden"));
  }, [showtimeId, seatId]);

  const inc = (pid) => setCart((c) => ({ ...c, [pid]: (c[pid] || 0) + 1 }));
  const dec = (pid) => setCart((c) => ({ ...c, [pid]: Math.max(0, (c[pid] || 0) - 1) }));

  const items = useMemo(() => Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity })), [cart]);
  const total = useMemo(() => {
    if (!ctx) return 0;
    return items.reduce((s, it) => {
      const p = ctx.products.find((x) => x.id === it.product_id);
      return s + (p ? p.sell_price * it.quantity : 0);
    }, 0);
  }, [items, ctx]);

  const submit = async () => {
    if (!items.length) return toast.error("Bitte Artikel wählen");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/seat-order`, {
        showtime_id: showtimeId, seat_id: seatId,
        guest_name: name.trim() || undefined,
        items, note: note.trim() || undefined,
      });
      setOk(data);
      toast.success("Bestellung aufgegeben — Personal bringt es an deinen Platz.");
    } catch (e) { toast.error(e.response?.data?.detail || "Bestellung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (!ctx) return <div className="min-h-screen bg-[#050505] text-[#A8A398] flex items-center justify-center">Lade …</div>;

  if (ok) return (
    <div className="min-h-screen bg-[#050505] text-[#F5F0E6] flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center space-y-4" data-testid="seat-order-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
          <Popcorn size={30} className="text-[#D4AF37]" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Bestellung #{ok.number}</div>
        <div className="font-heading text-3xl">Kommt an Platz {seatId}</div>
        <div className="text-sm text-[#A8A398]">Personal bringt {items.length} Artikel · {total.toFixed(2)} €</div>
        <div className="text-[10px] text-[#575148] uppercase tracking-widest">Ohne Aufstehen · Ohne Warteschlange</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F0E6] pb-32" data-testid="seat-order-page">
      <div className="max-w-lg mx-auto px-4 pt-6">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Snack am Platz</div>
        <div className="font-heading text-3xl mt-1">{ctx.movie.title}</div>
        <div className="text-sm text-[#A8A398] mt-1">{ctx.showtime.date} · {ctx.showtime.start_time} · {ctx.showtime.hall} · <b className="text-[#EEDC82]">Platz {seatId}</b></div>

        {ctx.orders_limit && (
          <div className={`mt-3 text-[10px] uppercase tracking-[0.3em] px-3 py-2 rounded border inline-flex items-center gap-2 ${ctx.orders_remaining === 0 ? "border-[#EE9CA1]/60 text-[#EE9CA1]" : "border-[#8FBC8F]/40 text-[#8FBC8F]"}`}
            data-testid="seat-order-limit">
            <ShoppingBag size={12} /> {ctx.orders_used || 0}/{ctx.orders_limit} Bestellungen genutzt
            {ctx.orders_remaining === 0 && <span className="text-[#EE9CA1]/80 normal-case tracking-normal ml-2">· Limit erreicht — bitte an der Kasse bestellen</span>}
          </div>
        )}

        <div className="mt-6 space-y-2">
          {ctx.products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3" data-testid={`seat-order-product-${p.id}`}>
              {p.image_url ? <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded" /> : <div className="w-12 h-12 rounded bg-[#141414] flex items-center justify-center text-[#575148]"><Popcorn size={20} /></div>}
              <div className="flex-1">
                <div className="text-sm">{p.name}</div>
                <div className="text-[11px] text-[#D4AF37] font-mono">{Number(p.sell_price).toFixed(2)} €</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => dec(p.id)} className="w-8 h-8 rounded border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid={`seat-order-dec-${p.id}`}><Minus size={14} className="mx-auto" /></button>
                <span className="w-5 text-center font-mono text-sm">{cart[p.id] || 0}</span>
                <button onClick={() => inc(p.id)} className="w-8 h-8 rounded bg-[#D4AF37] text-black hover:bg-[#EEDC82]" data-testid={`seat-order-inc-${p.id}`}><Plus size={14} className="mx-auto" /></button>
              </div>
            </div>
          ))}
          {!ctx.products.length && <div className="text-center py-10 text-[#575148]">Keine Snacks verfügbar</div>}
        </div>

        <div className="mt-6 space-y-2">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Dein Name (optional)"
            data-testid="seat-order-name" className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} placeholder="Hinweis (Allergien, Wünsche …)"
            data-testid="seat-order-note" className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
        </div>
      </div>

      {items.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 border-t border-[#1A1A1A] bg-[#0A0A0A]/95 backdrop-blur px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">{items.reduce((s, it) => s + it.quantity, 0)} Artikel</div>
              <div className="font-heading text-2xl text-[#D4AF37]">{total.toFixed(2)} €</div>
            </div>
            <button onClick={submit} disabled={busy || ctx.orders_remaining === 0} data-testid="seat-order-submit"
              title={ctx.orders_remaining === 0 ? "Bestell-Limit für diesen Sitz erreicht" : ""}
              className="px-5 py-3 rounded-lg bg-[#D4AF37] text-black uppercase text-[11px] tracking-[0.3em] hover:bg-[#EEDC82] disabled:opacity-50 flex items-center gap-2">
              <Send size={14} /> {busy ? "…" : ctx.orders_remaining === 0 ? "Limit erreicht" : "An Platz bestellen"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
